/**
 * Branding (MC3) : répartition sur la période, et écritures.
 *
 * Les écritures invalident toutes les entrées `branding-stats` : le Résumé et
 * la page Shopify lisent la même requête, et une dépense saisie doit y peser
 * sans rechargement.
 */

import type { BrandingExpense, BrandingExpenseInput, BrandingStats } from '@shared/types/branding';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ApiRequestError, createBranding, deleteBranding, fetchBrandingStats, updateBranding } from '../lib/api';
import { useAuth } from './useAuth';
import { useSectionStats, type SectionStatsOptions } from './useSectionStats';

const BRANDING_KEY = 'branding-stats';

export function useBrandingStats(options: SectionStatsOptions) {
  return useSectionStats<BrandingStats>(BRANDING_KEY, fetchBrandingStats, options);
}

export function useBrandingMutations() {
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const requireToken = (): string => {
    if (!token) throw new ApiRequestError(401, 'Session expiree');
    return token;
  };
  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: [BRANDING_KEY] });
  };
  const create = useMutation<BrandingExpense, Error, BrandingExpenseInput>({
    mutationFn: async (input) => (await createBranding(input, requireToken())).expense,
    onSuccess: invalidate,
  });
  const update = useMutation<BrandingExpense, Error, { id: number; input: BrandingExpenseInput }>({
    mutationFn: async ({ id, input }) => (await updateBranding(id, input, requireToken())).expense,
    onSuccess: invalidate,
  });
  const remove = useMutation<void, Error, number>({
    mutationFn: (id) => deleteBranding(id, requireToken()),
    onSuccess: invalidate,
  });
  return {
    create: (input: BrandingExpenseInput) => create.mutateAsync(input),
    update: (id: number, input: BrandingExpenseInput) => update.mutateAsync({ id, input }),
    remove: (id: number) => remove.mutateAsync(id),
    isSaving: create.isPending || update.isPending || remove.isPending,
  };
}
